import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle,
  XCircle,
  Clock,
  Shield,
  LogIn,
  UserPlus,
  Loader2,
  AlertTriangle,
  Lock,
  Mail,
} from 'lucide-react';
import { toast } from 'sonner';

import { vaultService } from '@/services/vault.service';
import { VaultInvitation, InvitationStatus } from '@/types/vault.types';
import { useAuthStore } from '@/store/auth.store';
import { ROUTES } from '@/constants/app';

export const InvitationAcceptPage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user } = useAuthStore();

  const [invitation, setInvitation] = useState<VaultInvitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t('vault.invitation.invalid.missingToken'));
      setLoading(false);
      return;
    }

    const fetchInvitation = async () => {
      try {
        const data = await vaultService.getInvitationByToken(token);
        setInvitation(data);
      } catch (cause) {
        const err = cause as HttpApiError;
        console.error('Error fetching invitation:', err);
        setError(
          err.response?.data?.message ||
            t('vault.invitation.invalid.notFoundOrExpired')
        );
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [token, t]);

  const handleAccept = async () => {
    if (!token) return;

    setAccepting(true);
    try {
      await vaultService.acceptInvitation(token);
      toast.success(t('vault.invitation.accepted.success'));
      navigate('/my-vault');
    } catch (cause) {
      const err = cause as HttpApiError;
      console.error('Error accepting invitation:', err);
      toast.error(
        err.response?.data?.message || t('vault.invitation.accepted.error')
      );
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = async () => {
    if (!token) return;

    setDeclining(true);
    try {
      await vaultService.declineInvitation(token);
      toast.success(t('vault.invitation.declined.success'));
      navigate(ROUTES.DASHBOARD);
    } catch (cause) {
      const err = cause as HttpApiError;
      console.error('Error declining invitation:', err);
      toast.error(
        err.response?.data?.message || t('vault.invitation.declined.error')
      );
    } finally {
      setDeclining(false);
    }
  };

  const isExpired = invitation
    ? new Date(invitation.expiresAt) < new Date()
    : false;

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return t('vault.invitation.pending.notDefined');
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return t('vault.invitation.pending.invalidDate');
    return date.toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-100 p-4">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="text-primary-600 h-10 w-10 animate-spin" />
          <p className="text-sm text-neutral-600">
            {t('vault.invitation.loading')}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !invitation) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-100 p-4">
        <div className="w-full max-w-md rounded-xl border border-neutral-200 bg-white p-8">
          <div className="flex flex-col items-center text-center">
            <div className="bg-error-50 mb-4 flex h-12 w-12 items-center justify-center rounded-full">
              <XCircle className="text-error-600 h-6 w-6" />
            </div>
            <h1 className="mb-2 text-xl font-semibold text-neutral-900">
              {t('vault.invitation.invalid.title')}
            </h1>
            <p className="mb-6 text-sm text-neutral-600">
              {error || t('vault.invitation.invalid.description')}
            </p>
            <Link
              to={ROUTES.AUTH.LOGIN}
              className="bg-primary-600 hover:bg-primary-700 rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-colors"
            >
              {t('vault.invitation.actions.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Already accepted
  if (invitation.status === InvitationStatus.ACCEPTED) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-100 p-4">
        <div className="w-full max-w-md rounded-xl border border-neutral-200 bg-white p-8">
          <div className="flex flex-col items-center text-center">
            <div className="bg-success-50 mb-4 flex h-12 w-12 items-center justify-center rounded-full">
              <CheckCircle className="text-success-600 h-6 w-6" />
            </div>
            <h1 className="mb-2 text-xl font-semibold text-neutral-900">
              {t('vault.invitation.accepted.title')}
            </h1>
            <p className="mb-6 text-sm text-neutral-600">
              {t('vault.invitation.accepted.description')}
            </p>
            <Link
              to={`/vault/${invitation.vaultId}`}
              className="bg-primary-600 hover:bg-primary-700 rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-colors"
            >
              {t('vault.invitation.accepted.accessVault')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Declined
  if (invitation.status === InvitationStatus.DECLINED) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-100 p-4">
        <div className="w-full max-w-md rounded-xl border border-neutral-200 bg-white p-8">
          <div className="flex flex-col items-center text-center">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100">
              <XCircle className="h-6 w-6 text-neutral-500" />
            </div>
            <h1 className="mb-2 text-xl font-semibold text-neutral-900">
              {t('vault.invitation.declined.title')}
            </h1>
            <p className="mb-6 text-sm text-neutral-600">
              {t('vault.invitation.declined.description')}
            </p>
            <Link
              to={ROUTES.AUTH.LOGIN}
              className="bg-primary-600 hover:bg-primary-700 rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-colors"
            >
              {t('vault.invitation.actions.backToHome')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Expired
  if (isExpired || invitation.status === InvitationStatus.EXPIRED) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-100 p-4">
        <div className="w-full max-w-md rounded-xl border border-neutral-200 bg-white p-8">
          <div className="flex flex-col items-center text-center">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50">
              <Clock className="h-6 w-6 text-amber-600" />
            </div>
            <h1 className="mb-2 text-xl font-semibold text-neutral-900">
              {t('vault.invitation.expired.title')}
            </h1>
            <p className="mb-6 text-sm text-neutral-600">
              {t('vault.invitation.expired.description', {
                date: formatDate(invitation.expiresAt),
              })}
              <br />
              {t('vault.invitation.expired.contact')}
            </p>
            <Link
              to={ROUTES.AUTH.LOGIN}
              className="bg-primary-600 hover:bg-primary-700 rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-colors"
            >
              {t('vault.invitation.actions.backToHome')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Valid pending invitation
  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-100 p-4">
      <div className="w-full max-w-lg rounded-xl border border-neutral-200 bg-white">
        {/* Header */}
        <div className="border-b border-neutral-200 p-6">
          <div className="flex items-center gap-4">
            <div className="bg-primary-50 flex h-12 w-12 items-center justify-center rounded-lg">
              <Shield className="text-primary-600 h-6 w-6" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-neutral-900">
                {t('vault.invitation.pending.title')}
              </h1>
              {invitation.vault?.name && (
                <p className="text-sm text-neutral-600">
                  {invitation.vault.name}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Invitation details */}
          <div className="mb-6 space-y-4">
            {invitation.message && (
              <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                <p className="mb-1 text-xs font-medium tracking-wide text-neutral-500 uppercase">
                  {t('vault.invitation.pending.message')}
                </p>
                <p className="text-sm text-neutral-700">{invitation.message}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="mb-1 text-xs font-medium tracking-wide text-neutral-500 uppercase">
                  {t('vault.invitation.pending.expiresAt')}
                </p>
                <p className="text-sm font-medium text-neutral-900">
                  {formatDate(invitation.expiresAt)}
                </p>
              </div>

              {(invitation.firstName || invitation.lastName) && (
                <div>
                  <p className="mb-1 text-xs font-medium tracking-wide text-neutral-500 uppercase">
                    {t('vault.invitation.pending.invitedFor')}
                  </p>
                  <p className="text-sm font-medium text-neutral-900">
                    {[invitation.firstName, invitation.lastName]
                      .filter(Boolean)
                      .join(' ')}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Actions based on auth status */}
          {isAuthenticated ? (
            <div className="space-y-4">
              <div className="border-success-200 bg-success-50 text-success-700 flex items-center gap-2 rounded-lg border p-3 text-sm">
                <Mail className="h-4 w-4" />
                <span>
                  {t('vault.invitation.auth.loggedInAs')}{' '}
                  <strong>{user?.email}</strong>
                </span>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleDecline}
                  disabled={declining || accepting}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-neutral-300 bg-white px-4 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:opacity-50"
                >
                  {declining ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <XCircle className="h-4 w-4" />
                  )}
                  {t('vault.invitation.actions.decline')}
                </button>

                <button
                  onClick={handleAccept}
                  disabled={accepting || declining}
                  className="bg-primary-600 hover:bg-primary-700 flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white transition-colors disabled:opacity-50"
                >
                  {accepting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4" />
                  )}
                  {t('vault.invitation.actions.accept')}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
                <AlertTriangle className="h-4 w-4" />
                <span>{t('vault.invitation.auth.loginRequired')}</span>
              </div>

              <div className="flex gap-3">
                <Link
                  to={`${ROUTES.AUTH.LOGIN}?redirect=/vault/invitation/${token}`}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-neutral-300 bg-white px-4 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
                >
                  <LogIn className="h-4 w-4" />
                  {t('vault.invitation.auth.loginButton')}
                </Link>

                <Link
                  to={`/register/invitation/${token}`}
                  className="bg-primary-600 hover:bg-primary-700 flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white transition-colors"
                >
                  <UserPlus className="h-4 w-4" />
                  {t('vault.invitation.auth.createAccount')}
                </Link>
              </div>

              <p className="text-center text-xs text-neutral-500">
                {t('vault.invitation.auth.autoLink')}
              </p>
            </div>
          )}
        </div>

        {/* Footer - Security note */}
        <div className="border-t border-neutral-200 bg-neutral-50 px-6 py-4">
          <div className="flex items-start gap-3 text-xs text-neutral-600">
            <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-neutral-400" />
            <p>{t('vault.invitation.security.message')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
